// Platform Configuration for Settings Module
// This file contains all platform-specific configuration including:
// - Platform/entrypoint support mappings
// - Category availability per platform/entrypoint
// - Settings lookup tree (which settings are available where)

import {
	NativePlatformType,
	SettingsEntryType,
	NativePlatform,
	SettingsEntry,
} from '../Contracts/base';
import { SettingsCategory } from './SettingsCategory';
import {
	DISPLAY_SETTING_IDS,
	KEYBOARD_SETTING_IDS,
	PRIVACY_SECURITY_SETTING_IDS,
	TLS_SETTING_IDS,
	WORKSPACE_UPDATE_SETTING_IDS,
	GENERAL_SETTING_IDS,
	SHORTCUT_SETTING_IDS,
	CAMERA_SETTING_IDS,
	AUDIO_SETTING_IDS,
	DEVICE_SETTING_IDS,
	MOUSE_SETTING_IDS,
	ACCOUNT_MANAGEMENT_SETTING_IDS,
	SUPPORT_AND_TROUBLESHOOT_SETTING_IDS,
	COLLECT_LOGS_SETTING_IDS,
} from './SettingIds';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Platform and Settings Entry Point combinations
export interface PlatformEntryMapping {
	platform: NativePlatformType;
	supportedEntrypoints: SettingsEntryType[];
	description: string;
}

// Settings category mapping for platform + entry point combinations
export interface CategoryMapping {
	platform: NativePlatformType;
	entrypoint: SettingsEntryType;
	categories: SettingsCategory[];
}

/**
 * Tree structure that maps Platform → Entrypoint → Category → Setting IDs
 * This defines the hierarchy and availability of settings across different contexts
 */
export type SettingsLookupTree = {
	[K in NativePlatformType]?: {
		[E in SettingsEntryType]?: {
			[category: string]: string[]; // Array of setting IDs for each category
		};
	};
};

// ============================================================================
// PLATFORM ENTRYPOINT MAPPINGS
// ============================================================================

// Complete platform and entrypoint mappings
export const PLATFORM_ENTRY_MAPPINGS: PlatformEntryMapping[] = [
	{
		platform: NativePlatform.WINDOWS,
		supportedEntrypoints: [
			SettingsEntry.ENDPOINT,
			SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
			SettingsEntry.IN_SESSION,
			SettingsEntry.ACCOUNT_MANAGEMENT,
		],
		description: 'Windows desktop application - supports all entrypoints',
	},
	{
		platform: NativePlatform.LINUX,
		supportedEntrypoints: [
			SettingsEntry.ENDPOINT,
			SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
			SettingsEntry.IN_SESSION,
			SettingsEntry.ACCOUNT_MANAGEMENT,
		],
		description: 'Linux desktop application - supports all entrypoints',
	},
	{
		platform: NativePlatform.MAC,
		supportedEntrypoints: [
			SettingsEntry.ENDPOINT,
			SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
			SettingsEntry.IN_SESSION,
			SettingsEntry.ACCOUNT_MANAGEMENT,
		],
		description: 'macOS desktop application - supports all entrypoints',
	},
	{
		platform: NativePlatform.IOS,
		supportedEntrypoints: [SettingsEntry.IN_SESSION, SettingsEntry.ACCOUNT_MANAGEMENT],
		description: 'iOS mobile application - supports in-session and account management',
	},
	{
		platform: NativePlatform.ANDROID,
		supportedEntrypoints: [SettingsEntry.IN_SESSION, SettingsEntry.ACCOUNT_MANAGEMENT],
		description:
			'Android mobile application - supports in-session and account management',
	},
	{
		platform: NativePlatform.HTML5,
		supportedEntrypoints: [
			SettingsEntry.IN_SESSION,
			SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
		],
		description: 'HTML5 web client - supports in-session and support settings',
	},
	{
		platform: NativePlatform.CHROMEOS,
		supportedEntrypoints: [
			SettingsEntry.ENDPOINT,
			SettingsEntry.IN_SESSION,
			SettingsEntry.ACCOUNT_MANAGEMENT,
		],
		description:
			'ChromeOS application - supports endpoint, in-session, and account management',
	},
];

// ============================================================================
// PLATFORM CATEGORY MAPPINGS
// ============================================================================

// Platform + Entry Point + Categories mapping
export const PLATFORM_CATEGORY_MAPPINGS: CategoryMapping[] = [
	// Windows Platform
	{
		platform: NativePlatform.WINDOWS,
		entrypoint: SettingsEntry.ENDPOINT,
		categories: [
			SettingsCategory.General,
			SettingsCategory.Keyboard,
			SettingsCategory.Display,
			SettingsCategory.PrivacySecurity,
		],
	},
	{
		platform: NativePlatform.WINDOWS,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [
			SettingsCategory.SoundAndCamera,
			SettingsCategory.Mouse,
			SettingsCategory.Devices,
			SettingsCategory.Display,
			SettingsCategory.PrivacySecurity,
		],
	},
	{
		platform: NativePlatform.WINDOWS,
		entrypoint: SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
		categories: [
			SettingsCategory.CollectLogs,
			SettingsCategory.SendFeedback,
			SettingsCategory.CheckConfiguration,
			SettingsCategory.ResetCWA,
			SettingsCategory.HelpDocumentation,
		],
	},
	{
		platform: NativePlatform.WINDOWS,
		entrypoint: SettingsEntry.ACCOUNT_MANAGEMENT,
		categories: [SettingsCategory.AccountManagement, SettingsCategory.AddStore],
	},

	// Linux Platform
	{
		platform: NativePlatform.LINUX,
		entrypoint: SettingsEntry.ENDPOINT,
		categories: [
			SettingsCategory.General,
			SettingsCategory.Keyboard,
			SettingsCategory.Display,
			SettingsCategory.PrivacySecurity,
		],
	},
	{
		platform: NativePlatform.LINUX,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [SettingsCategory.Display, SettingsCategory.Keyboard],
	},
	{
		platform: NativePlatform.LINUX,
		entrypoint: SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
		categories: [
			SettingsCategory.CollectLogs,
			SettingsCategory.SendFeedback,
			SettingsCategory.CheckConfiguration,
			SettingsCategory.ResetCWA,
			SettingsCategory.HelpDocumentation,
		],
	},
	{
		platform: NativePlatform.LINUX,
		entrypoint: SettingsEntry.ACCOUNT_MANAGEMENT,
		categories: [SettingsCategory.AccountManagement, SettingsCategory.AddStore],
	},

	// Mac Platform
	{
		platform: NativePlatform.MAC,
		entrypoint: SettingsEntry.ENDPOINT,
		categories: [
			SettingsCategory.General,
			SettingsCategory.SoundAndCamera,
			SettingsCategory.Keyboard,
			SettingsCategory.Devices,
			SettingsCategory.Display,
			SettingsCategory.PrivacySecurity,
		],
	},
	{
		platform: NativePlatform.MAC,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [
			SettingsCategory.General,
			SettingsCategory.SoundAndCamera,
			SettingsCategory.Keyboard,
			SettingsCategory.Devices,
			SettingsCategory.Display,
			SettingsCategory.PrivacySecurity,
		],
	},
	{
		platform: NativePlatform.MAC,
		entrypoint: SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
		categories: [
			SettingsCategory.CollectLogs,
			SettingsCategory.SendFeedback,
			SettingsCategory.CheckConfiguration,
			SettingsCategory.ResetCWA,
			SettingsCategory.HelpDocumentation,
		],
	},
	{
		platform: NativePlatform.MAC,
		entrypoint: SettingsEntry.ACCOUNT_MANAGEMENT,
		categories: [SettingsCategory.AccountManagement, SettingsCategory.AddStore],
	},

	// iOS Platform (Mobile - limited categories)
	{
		platform: NativePlatform.IOS,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [SettingsCategory.Display],
	},
	{
		platform: NativePlatform.IOS,
		entrypoint: SettingsEntry.ACCOUNT_MANAGEMENT,
		categories: [SettingsCategory.AccountManagement, SettingsCategory.AddStore],
	},

	// Android Platform (Mobile - limited categories)
	{
		platform: NativePlatform.ANDROID,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [SettingsCategory.Display],
	},
	{
		platform: NativePlatform.ANDROID,
		entrypoint: SettingsEntry.ACCOUNT_MANAGEMENT,
		categories: [SettingsCategory.AccountManagement, SettingsCategory.AddStore],
	},

	// HTML5 Platform (Web - limited categories)
	{
		platform: NativePlatform.HTML5,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [SettingsCategory.Display],
	},
	{
		platform: NativePlatform.HTML5,
		entrypoint: SettingsEntry.SUPPORT_AND_TROUBLESHOOT,
		categories: [
			SettingsCategory.CollectLogs,
			SettingsCategory.SendFeedback,
			SettingsCategory.CheckConfiguration,
			SettingsCategory.ResetCWA,
			SettingsCategory.HelpDocumentation,
		],
	},

	// ChromeOS Platform
	{
		platform: NativePlatform.CHROMEOS,
		entrypoint: SettingsEntry.ENDPOINT,
		categories: [SettingsCategory.General, SettingsCategory.PrivacySecurity],
	},
	{
		platform: NativePlatform.CHROMEOS,
		entrypoint: SettingsEntry.IN_SESSION,
		categories: [SettingsCategory.Display],
	},
	{
		platform: NativePlatform.CHROMEOS,
		entrypoint: SettingsEntry.ACCOUNT_MANAGEMENT,
		categories: [SettingsCategory.AccountManagement, SettingsCategory.AddStore],
	},
];

// ============================================================================
// SETTINGS LOOKUP TREE
// ============================================================================

/**
 * Centralized Settings Lookup Tree
 * Maps the hierarchical structure: Platform → Entrypoint → Category → Setting IDs
 * This defines which settings are available in which context
 */
export const SETTINGS_LOOKUP: SettingsLookupTree = {
	[NativePlatform.WINDOWS]: {
		[SettingsEntry.ENDPOINT]: {
			[SettingsCategory.Display]: [DISPLAY_SETTING_IDS.SESSION_RESOLUTION],
			[SettingsCategory.Keyboard]: [
				KEYBOARD_SETTING_IDS.SYSTEM_SHORTCUTS,
				KEYBOARD_SETTING_IDS.LAYOUT_SYNC,
				KEYBOARD_SETTING_IDS.IME,
				KEYBOARD_SETTING_IDS.SHOW_LANGUAGE_BAR,
			],
			[SettingsCategory.WorkspaceUpdate]: [
				WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES,
				WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
				WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL_CONFIG,
			],
			[SettingsCategory.General]: [
				GENERAL_SETTING_IDS.WORKSPACE_UPDATE,
				GENERAL_SETTING_IDS.SHORTCUTS,
				GENERAL_SETTING_IDS.SESSION_RECONNECT,
				GENERAL_SETTING_IDS.ABOUT,
			],
			[SettingsCategory.Shortcuts]: [
				SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING,
				SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING,
				SHORTCUT_SETTING_IDS.ORGANIZE_BY_CATEGORIES,
				SHORTCUT_SETTING_IDS.ORGANIZE_START_MENU_CATEGORIES,
				SHORTCUT_SETTING_IDS.ORGANIZE_DESKTOP_CATEGORIES,
			],
			[SettingsCategory.PrivacySecurity]: [
				PRIVACY_SECURITY_SETTING_IDS.SEND_USAGE_STATISTICS,
				PRIVACY_SECURITY_SETTING_IDS.TRIGGER_DELETE_PASSWORDS,
				PRIVACY_SECURITY_SETTING_IDS.SSO_ENTRA_ID,
			],
		},
		[SettingsEntry.IN_SESSION]: {
			[SettingsCategory.SoundAndCamera]: [
				CAMERA_SETTING_IDS.AVAILABLE_CAMERA,
				AUDIO_SETTING_IDS.DEFAULT_SPEAKER,
				AUDIO_SETTING_IDS.DEFAULT_MICROPHONE,
				AUDIO_SETTING_IDS.NOISE_SUPPRESSION,
			],
			[SettingsCategory.Mouse]: [
				MOUSE_SETTING_IDS.USE_RELATIVE_MOUSE,
				MOUSE_SETTING_IDS.CONSTRAIN_POINTER_TO_WINDOW,
				MOUSE_SETTING_IDS.APPLY_WINDOW_SCALING,
			],
			[SettingsCategory.Devices]: [
				DEVICE_SETTING_IDS.DEVICES_INFO,
				DEVICE_SETTING_IDS.AUTO_CONNECT_ON_SESSION_START,
				DEVICE_SETTING_IDS.AUTO_CONNECT_NEW_DEVICE,
			],
			[SettingsCategory.Display]: [
				DISPLAY_SETTING_IDS.MONITOR_LAYOUT,
				DISPLAY_SETTING_IDS.DESKTOP_DISPLAY,
			],
			[SettingsCategory.PrivacySecurity]: [
				PRIVACY_SECURITY_SETTING_IDS.WINDOWS_PERMISSIONS,
			],
		},
		[SettingsEntry.ACCOUNT_MANAGEMENT]: {
			[SettingsCategory.AccountManagement]: [
				ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST,
				ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
			],
			[SettingsCategory.AddStore]: [ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE],
		},
		[SettingsEntry.SUPPORT_AND_TROUBLESHOOT]: {
			[SettingsCategory.CollectLogs]: [COLLECT_LOGS_SETTING_IDS.LOG_COLLECTION],
			[SettingsCategory.SendFeedback]: [
				SUPPORT_AND_TROUBLESHOOT_SETTING_IDS.SEND_FEEDBACK,
			],
			[SettingsCategory.CheckConfiguration]: [
				SUPPORT_AND_TROUBLESHOOT_SETTING_IDS.CHECK_CONFIGURATION,
			],
			[SettingsCategory.ResetCWA]: [SUPPORT_AND_TROUBLESHOOT_SETTING_IDS.RESET_CWA],
			[SettingsCategory.HelpDocumentation]: [
				SUPPORT_AND_TROUBLESHOOT_SETTING_IDS.HELP_DOCUMENTATION,
			],
		},
	},
	[NativePlatform.LINUX]: {
		[SettingsEntry.ENDPOINT]: {
			[SettingsCategory.PrivacySecurity]: [
				PRIVACY_SECURITY_SETTING_IDS.LINUX_PRIVACY_FEATURE,
			],
		},
		[SettingsEntry.ACCOUNT_MANAGEMENT]: {
			[SettingsCategory.AccountManagement]: [
				ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST,
				ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
			],
			[SettingsCategory.AddStore]: [ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE],
		},
	},
	[NativePlatform.MAC]: {
		[SettingsEntry.ENDPOINT]: {
			[SettingsCategory.General]: [
				GENERAL_SETTING_IDS.WORKSPACE_UPDATE,
				GENERAL_SETTING_IDS.CONNECTION_NOTIFICATIONS,
				GENERAL_SETTING_IDS.START_ON_SYSTEM_STARTUP,
				GENERAL_SETTING_IDS.SHOW_IN_MENU_BAR,
				GENERAL_SETTING_IDS.MAC_SESSION_RECONNECT,
				GENERAL_SETTING_IDS.ABOUT,
			],
			[SettingsCategory.WorkspaceUpdate]: [
				WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES,
				WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
			],
			[SettingsCategory.Display]: [
				DISPLAY_SETTING_IDS.SESSION_RESOLUTION,
				DISPLAY_SETTING_IDS.CAST_TO_WORKSPACE_HUB,
			],
			[SettingsCategory.Keyboard]: [
				KEYBOARD_SETTING_IDS.SYSTEM_SHORTCUTS,
				KEYBOARD_SETTING_IDS.LAYOUT_SYNC,
				KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE,
				KEYBOARD_SETTING_IDS.SHOW_LANGUAGE_BAR,
			],
			[SettingsCategory.SoundAndCamera]: [CAMERA_SETTING_IDS.CAMERA_ACCESS],
			[SettingsCategory.Devices]: [
				DEVICE_SETTING_IDS.AUTO_CONNECT_ON_SESSION_START,
				DEVICE_SETTING_IDS.AUTO_CONNECT_NEW_DEVICE,
			],
			[SettingsCategory.TLS]: [
				TLS_SETTING_IDS?.TLS_REQUIRE_FOR_ALL,
				TLS_SETTING_IDS?.TLS_COMPLIANCE_MODE,
				TLS_SETTING_IDS?.TLS_CRYPTO_MODULE,
				TLS_SETTING_IDS?.TLS_VERSION,
				TLS_SETTING_IDS?.TLS_CERTIFICATE_REVOCATION,
			].filter(Boolean), // Remove undefined values
			[SettingsCategory.PrivacySecurity]: [
				PRIVACY_SECURITY_SETTING_IDS.SEND_USAGE_STATISTICS,
				PRIVACY_SECURITY_SETTING_IDS.TRIGGER_DELETE_PASSWORDS,
				PRIVACY_SECURITY_SETTING_IDS.TLS_Settings,
				PRIVACY_SECURITY_SETTING_IDS.SMARTCARD,
				PRIVACY_SECURITY_SETTING_IDS.PERMISSIONS,
			],
		},
		[SettingsEntry.IN_SESSION]: {
			[SettingsCategory.General]: [
				GENERAL_SETTING_IDS.WORKSPACE_UPDATE,
				GENERAL_SETTING_IDS.CONNECTION_NOTIFICATIONS,
				GENERAL_SETTING_IDS.START_ON_SYSTEM_STARTUP,
				GENERAL_SETTING_IDS.SHOW_IN_MENU_BAR,
				GENERAL_SETTING_IDS.MAC_SESSION_RECONNECT,
				GENERAL_SETTING_IDS.ABOUT,
			],
			[SettingsCategory.WorkspaceUpdate]: [
				WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES,
				WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
			],
			[SettingsCategory.Display]: [
				DISPLAY_SETTING_IDS.SESSION_RESOLUTION,
				DISPLAY_SETTING_IDS.CAST_TO_WORKSPACE_HUB,
			],
			[SettingsCategory.Keyboard]: [
				KEYBOARD_SETTING_IDS.SYSTEM_SHORTCUTS,
				KEYBOARD_SETTING_IDS.LAYOUT_SYNC,
				KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE,
				KEYBOARD_SETTING_IDS.SHOW_LANGUAGE_BAR,
			],
			[SettingsCategory.SoundAndCamera]: [
				CAMERA_SETTING_IDS.CAMERA_ACCESS,
				AUDIO_SETTING_IDS.DEFAULT_SPEAKER,
				AUDIO_SETTING_IDS.DEFAULT_MICROPHONE,
			],
			[SettingsCategory.Devices]: [
				DEVICE_SETTING_IDS.DEVICES_INFO,
				DEVICE_SETTING_IDS.AUTO_CONNECT_ON_SESSION_START,
				DEVICE_SETTING_IDS.AUTO_CONNECT_NEW_DEVICE,
			],
			[SettingsCategory.TLS]: [
				TLS_SETTING_IDS?.TLS_REQUIRE_FOR_ALL,
				TLS_SETTING_IDS?.TLS_COMPLIANCE_MODE,
				TLS_SETTING_IDS?.TLS_CRYPTO_MODULE,
				TLS_SETTING_IDS?.TLS_VERSION,
				TLS_SETTING_IDS?.TLS_CERTIFICATE_REVOCATION,
			].filter(Boolean), // Remove undefined values
			[SettingsCategory.PrivacySecurity]: [
				PRIVACY_SECURITY_SETTING_IDS.SEND_USAGE_STATISTICS,
				PRIVACY_SECURITY_SETTING_IDS.TRIGGER_DELETE_PASSWORDS,
				PRIVACY_SECURITY_SETTING_IDS.TLS_Settings,
				PRIVACY_SECURITY_SETTING_IDS.SMARTCARD,
				PRIVACY_SECURITY_SETTING_IDS.PERMISSIONS,
			],
		},
		[SettingsEntry.SUPPORT_AND_TROUBLESHOOT]: {},
		[SettingsEntry.ACCOUNT_MANAGEMENT]: {
			[SettingsCategory.AccountManagement]: [
				ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST,
				ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
			],
			[SettingsCategory.AddStore]: [ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE],
		},
	},
	[NativePlatform.IOS]: {
		[SettingsEntry.ACCOUNT_MANAGEMENT]: {
			[SettingsCategory.AccountManagement]: [
				ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST,
				ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
			],
			[SettingsCategory.AddStore]: [ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE],
		},
	},
	[NativePlatform.ANDROID]: {
		[SettingsEntry.ACCOUNT_MANAGEMENT]: {
			[SettingsCategory.AccountManagement]: [
				ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST,
				ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
			],
			[SettingsCategory.AddStore]: [ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE],
		},
	},
	[NativePlatform.HTML5]: {},
	[NativePlatform.CHROMEOS]: {
		[SettingsEntry.ACCOUNT_MANAGEMENT]: {
			[SettingsCategory.AccountManagement]: [
				ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST,
				ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
			],
			[SettingsCategory.AddStore]: [ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE],
		},
	},
};

// ============================================================================
// CATEGORY DESCRIPTIONS
// ============================================================================

// Category descriptions for documentation
export const CATEGORY_DESCRIPTIONS: Record<SettingsCategory, string> = {
	[SettingsCategory.General]:
		'Basic application settings and preferences - available on all platforms',
	[SettingsCategory.Keyboard]:
		'Keyboard shortcuts and input settings - desktop platforms only',
	[SettingsCategory.Display]: 'Display and appearance settings - desktop platforms only',
	[SettingsCategory.PrivacySecurity]:
		'Authentication and security settings - available on all platforms',
	[SettingsCategory.TLS]: 'TLS encryption and security settings - macOS platforms only',
	[SettingsCategory.WorkspaceUpdate]:
		'Application update settings and preferences - desktop platforms only',
	[SettingsCategory.Shortcuts]:
		'Application shortcut management settings - desktop platforms only',
	[SettingsCategory.SoundAndCamera]:
		'Audio and video device settings - macOS platforms only',
	[SettingsCategory.Devices]:
		'Connected device settings and preferences - macOS platforms only',
	[SettingsCategory.Mouse]: 'Mouse and pointing device settings',
	[SettingsCategory.AccountManagement]: 'Manage stores and account configuration',
	[SettingsCategory.AddStore]: 'Add a new store to your workspace',
	[SettingsCategory.CollectLogs]:
		'Collect and export application logs for troubleshooting',
	[SettingsCategory.SendFeedback]: 'Send feedback to Citrix about your experience',
	[SettingsCategory.CheckConfiguration]: 'Check and verify the application configuration',
	[SettingsCategory.ResetCWA]: 'Reset Citrix Workspace app to its default state',
	[SettingsCategory.HelpDocumentation]: 'Access help documentation and support resources',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get setting IDs from lookup tree for a specific platform/entrypoint/category
 */
export function getSettingIdsFromLookup(
	platform: NativePlatformType,
	entrypoint: SettingsEntryType,
	category: SettingsCategory
): string[] {
	return SETTINGS_LOOKUP[platform]?.[entrypoint]?.[category] || [];
}

/**
 * Check if a setting ID exists in a specific context
 */
export function isSettingAvailable(
	settingId: string,
	platform: NativePlatformType,
	entrypoint: SettingsEntryType,
	category: SettingsCategory
): boolean {
	const settingIds = getSettingIdsFromLookup(platform, entrypoint, category);
	return settingIds.includes(settingId);
}

/**
 * Get all categories that have settings for a platform/entrypoint
 */
export function getCategoriesWithSettings(
	platform: NativePlatformType,
	entrypoint: SettingsEntryType
): SettingsCategory[] {
	const entrypointTree = SETTINGS_LOOKUP[platform]?.[entrypoint];
	if (!entrypointTree) return [];

	return Object.keys(entrypointTree).filter((category) => {
		const settingIds = entrypointTree[category];
		return settingIds && settingIds.length > 0;
	}) as SettingsCategory[];
}

/**
 * Find all contexts (platform/entrypoint/category) where a setting ID is available
 */
export function findContextsForSettingId(settingId: string): Array<{
	platform: NativePlatformType;
	entrypoint: SettingsEntryType;
	category: SettingsCategory;
}> {
	const contexts: Array<{
		platform: NativePlatformType;
		entrypoint: SettingsEntryType;
		category: SettingsCategory;
	}> = [];

	for (const platform in SETTINGS_LOOKUP) {
		const platformTree = SETTINGS_LOOKUP[platform as NativePlatformType];
		if (!platformTree) continue;

		for (const entrypoint in platformTree) {
			const entrypointTree = platformTree[entrypoint as SettingsEntryType];
			if (!entrypointTree) continue;

			for (const category in entrypointTree) {
				const settingIds = entrypointTree[category];
				if (settingIds.includes(settingId)) {
					contexts.push({
						platform: platform as NativePlatformType,
						entrypoint: entrypoint as SettingsEntryType,
						category: category as SettingsCategory,
					});
				}
			}
		}
	}

	return contexts;
}

/**
 * Get supported entrypoints for a platform
 */
export function getSupportedEntrypoints(
	platform: NativePlatformType
): SettingsEntryType[] {
	const mapping = PLATFORM_ENTRY_MAPPINGS.find((p) => p.platform === platform);
	return mapping?.supportedEntrypoints || [];
}

/**
 * Check if platform supports a specific entrypoint
 */
export function isPlatformEntrypointSupported(
	platform: NativePlatformType,
	entrypoint: SettingsEntryType
): boolean {
	const supportedEntrypoints = getSupportedEntrypoints(platform);
	return supportedEntrypoints.includes(entrypoint);
}

/**
 * Get categories available for a platform + entrypoint combination
 */
export function getCategoriesForPlatformEntry(
	platform: NativePlatformType,
	entrypoint: SettingsEntryType
): SettingsCategory[] {
	const mapping = PLATFORM_CATEGORY_MAPPINGS.find(
		(m) => m.platform === platform && m.entrypoint === entrypoint
	);
	return mapping?.categories || [];
}

/**
 * Get setting configs for a specific platform/entrypoint/category
 * Combines lookup tree (for availability) with SettingsDefinitions (for configs)
 */
export function getSettingsForCategory(
	platform: NativePlatformType,
	entrypoint: SettingsEntryType,
	category: SettingsCategory,
	getSettingConfig: (id: string) => any
): Record<string, any> {
	const settingIds = getSettingIdsFromLookup(platform, entrypoint, category);
	const settings: Record<string, any> = {};

	settingIds.forEach((id) => {
		const config = getSettingConfig(id);
		if (config) {
			settings[id] = config;
		}
	});

	return settings;
}
